import {
  Persistence,
  PersistableObject,
  Either,
  left,
  right,
  safeStringify,
  safeParse,
  isLeft,
} from "@orgiggly/shared";
import { localStorageBudget } from "./localStorageBudget";

/**
 * Creates a LocalStorage implementation of PersistenceProvider
 */
export const createLocalStoragePersistence = (): Persistence => {
  return {
    async save(
      key: string,
      value: PersistableObject
    ): Promise<Either<void, string>> {
      try {
        const serialized = safeStringify(value);
        if (isLeft(serialized)) {
          return serialized;
        }

        // Shares the cache budget so app state can push out re-fetchable
        // cache entries rather than fail on a full origin (ORGIGGLY-WEB-11).
        if (!localStorageBudget().set(key, serialized.right)) {
          return left(
            "STORAGE_ERROR: Failed to save to localStorage: quota exceeded",
          );
        }
        window.dispatchEvent(
          new CustomEvent("orgiggly:localchange", { detail: { key } }),
        );
        return right(undefined);
      } catch (error) {
        return left(
          `STORAGE_ERROR: Failed to save to localStorage: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    },
    async saveImmediate(
      key: string,
      value: PersistableObject
    ): Promise<Either<void, string>> {
      return this.save(key, value);
    },

    async find(
      key: string,
      defaultValue?: PersistableObject
    ): Promise<Either<PersistableObject, string>> {
      try {
        const item = localStorage.getItem(key);
        if (item === null) {
          if (defaultValue !== undefined) {
            return right(defaultValue);
          }
          return left(`NOT_FOUND: Key "${key}" not found`);
        }

        const parsed = safeParse(item);
        if (isLeft(parsed)) {
          return parsed;
        }

        return right(parsed.right);
      } catch (error) {
        return left(
          `STORAGE_ERROR: Failed to read from localStorage: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    },

    async remove(key: string): Promise<Either<void, string>> {
      try {
        if (localStorage.getItem(key) === null) {
          return left(`NOT_FOUND: Key "${key}" not found`);
        }

        localStorage.removeItem(key);
        return right(undefined);
      } catch (error) {
        return left(
          `STORAGE_ERROR: Failed to remove from localStorage: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    },

    async list(): Promise<Either<string[], string>> {
      try {
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key) {
            keys.push(key);
          }
        }
        return right(keys);
      } catch (error) {
        return left(
          `STORAGE_ERROR: Failed to list localStorage keys: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    },

    async clear(): Promise<Either<void, string>> {
      try {
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key) {
            keys.push(key);
          }
        }

        keys.forEach((key) => localStorage.removeItem(key));
        return right(undefined);
      } catch (error) {
        return left(
          `STORAGE_ERROR: Failed to clear localStorage: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    },
  };
};

/**
 * Default instance of LocalStoragePersistence
 */
export const defaultLocalStoragePersistence = createLocalStoragePersistence();
